import clamp from 'lodash/clamp';
import get from 'lodash/get';
import update from 'lodash/update';
import { useCallback, useEffect, useState } from 'react';

import { CANVAS_OBJECT_TYPE_PREFIX, SELECTION_CIRCLES } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { CircleAction } from '~/core/enums';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectFabricCanvas,
  updateSelectionCircles,
} from '~/redux/slices/fabric';

type OptionId = string | number;
type Maybe<T> = T | undefined;

type DisplayData = {
  title: string;
  image: string;
  price: string;
  isDisabled?: boolean;
};

type OptionData<T> = {
  data: T;
  display: DisplayData;
};

export type useSidePanelSectionTabArgs<T> = {
  options: T[];
  optionsCounters: Record<OptionId, number>;
  isFetching?: boolean;
  getId: (option: T) => OptionId;
  mapOptionToDisplay: (option: T) => DisplayData;
  defaultOption?: T;
  ignoreDefaultOptionOnRemoval?: boolean;
  stateAddOption: (option: T) => void;
  stateRemoveOption: (option: T) => void;
  countersLimit?: number;
};

export const useSidePanelSectionTab = <T>({
  countersLimit = 999,
  options,
  optionsCounters,
  isFetching = false,
  getId,
  mapOptionToDisplay,
  defaultOption,
  ignoreDefaultOptionOnRemoval = false,
  stateAddOption,
  stateRemoveOption,
}: useSidePanelSectionTabArgs<T>) => {
  const dispatch = useAppDispatch();
  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<Maybe<OptionId>>();
  const [optionIndexOn3DModal, setOptionIndexOn3DModal] =
    useState<Maybe<number>>();
  const [prevQtyLimitIds, setPrevQtyLimitIds] = useState<{
    [K in 'oneToZero' | 'oneToMax']: OptionId[];
  }>({ oneToZero: [], oneToMax: [] });
  const optionsById = options.reduce<Record<OptionId, OptionData<T>>>(
    (z, option) =>
      update(z, getId(option), () => ({
        data: option,
        display: mapOptionToDisplay(option),
      })),
    {}
  );
  const getQty = useCallback(
    (id?: OptionId): number =>
      clamp(get(optionsCounters, id ?? -1, 0), 0, countersLimit),
    [optionsCounters, countersLimit]
  );
  const defaultOptionId = defaultOption ? getId(defaultOption) : undefined;
  const selectedOptionQty = getQty(selectedOptionId);
  const isSelectedOptionAtMaxQty = selectedOptionQty === countersLimit;
  const isSelectedOptionAtZeroQty = selectedOptionQty === 0;
  const isSelectedOption = useCallback(
    (id: OptionId) => selectedOptionId === id,
    [selectedOptionId]
  );

  const openRemoveModal = useCallback(() => setIsOpenRemoveModal(true), []);
  const closeRemoveModal = useCallback(() => setIsOpenRemoveModal(false), []);

  const prepareToAdd = (optionId: OptionId, qty: number) => {
    if (qty >= countersLimit) {
      return undefined;
    }

    return () => {
      stateAddOption(optionsById[optionId].data);
      setSelectedOptionId(optionId);
      setIsRemoving(false);
    };
  };

  const prepareToRemove = (optionId: OptionId) => {
    if (
      optionId === defaultOptionId ||
      (isSelectedOption(optionId) && isSelectedOptionAtZeroQty)
    ) {
      return undefined;
    }

    return () => {
      const option = ignoreDefaultOptionOnRemoval
        ? optionsById[optionId].data
        : (defaultOption ?? optionsById[optionId].data);

      openRemoveModal();
      stateRemoveOption(option);
      setSelectedOptionId(optionId);
      setIsRemoving(true);
    };
  };

  const canvas = useAppSelector(selectFabricCanvas);

  const clearSelectionCircles = useCallback(() => {
    const selectionCircles = {
      selection: SELECTION_CIRCLES.FOR_NONE,
      action: CircleAction.NONE,
    };

    dispatch(updateSelectionCircles(selectionCircles));
    drawingTool.removeObjectByName(
      canvas,
      CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
    );
    setIsRemoving(false);
  }, [canvas, dispatch]);

  /**
   * clear selection circles (blue dots) on mount and unmount
   */
  useEffect(() => {
    clearSelectionCircles();

    return () => clearSelectionCircles();
  }, [clearSelectionCircles]);

  /**
   * Clear selection circles (blue dots) when corner options have reached its
   * limit, meaning: no more options can be applied to any shape
   */
  useEffect(() => {
    const [oneToMax, oneToZero] = Object.entries(optionsCounters).reduce<
      [OptionId[], OptionId[]]
    >(
      ([toMax, toZero], [id, qty]) => [
        qty === countersLimit - 1 ? [...toMax, id] : toMax,
        qty === 1 ? [...toZero, id] : toZero,
      ],
      [[], []]
    );
    const hasIncreasedOptToMax =
      prevQtyLimitIds.oneToMax.includes(selectedOptionId ?? -1) &&
      isSelectedOptionAtMaxQty;
    const hasDecreasedOptToZero =
      prevQtyLimitIds.oneToZero.includes(selectedOptionId ?? -1) &&
      isSelectedOptionAtZeroQty;
    const hasChangedQtyLimits =
      [...oneToZero, ...oneToMax].sort().toString() !==
      [...prevQtyLimitIds.oneToZero, ...prevQtyLimitIds.oneToMax]
        .sort()
        .toString();

    if (
      hasDecreasedOptToZero ||
      hasIncreasedOptToMax ||
      (isSelectedOptionAtMaxQty && !isRemoving)
    ) {
      setSelectedOptionId(undefined);
      clearSelectionCircles();
    }

    if (hasChangedQtyLimits) {
      setPrevQtyLimitIds({ oneToZero, oneToMax });
    }
  }, [
    canvas,
    dispatch,
    prevQtyLimitIds.oneToZero,
    prevQtyLimitIds.oneToMax,
    optionsCounters,
    selectedOptionId,
    countersLimit,
    defaultOptionId,
    clearSelectionCircles,
    selectedOptionQty,
    isSelectedOptionAtMaxQty,
    isSelectedOptionAtZeroQty,
    isRemoving,
  ]);

  return {
    isFetching,
    options,
    modalFor3DCarousel: {
      isOpen: optionIndexOn3DModal !== undefined,
      optionIndex: optionIndexOn3DModal,
      setOptionIndex: setOptionIndexOn3DModal,
      close: () => setOptionIndexOn3DModal(undefined),
    },
    modalForOptionRemoval: {
      isOpen: isOpenRemoveModal,
      close: closeRemoveModal,
    },
    getQty,
    prepareToAdd,
    prepareToRemove,
    optionsById,
    selectedOptionId,
    setSelectedOptionId,
  };
};
